import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, Download, Film, Eye, FileText, Smartphone, Image as ImageIcon,
  MessageSquare, Send, Trash2, Lock, LogIn, CopyPlus, Clapperboard,
} from 'lucide-react';
import {
  getSharedProject, getSharedDownloadPDFUrl, getSharedDownloadZIPUrl,
  getSharedDownloadZIPThumbnailsUrl, getSharedDownloadEditUrl, verifySharePassword,
  getSharedComments, addSharedComment, deleteSharedComment, duplicateProject,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ThemeToggle from '../components/ThemeToggle';
import { formatDuration } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_KEY = (token) => `share_access_${token}`;

// ── Comment component ────────────────────────────────────────────────────────
function CommentItem({ comment, onDelete, isOwnerOrAuthor }) {
  const date = new Date(comment.created_at).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
  return (
    <div className="group flex gap-2.5">
      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5 overflow-hidden">
        {comment.author_picture
          ? <img src={comment.author_picture} alt="" className="w-full h-full object-cover" />
          : <span className="text-[10px] font-semibold text-primary">{(comment.author_name || '?')[0].toUpperCase()}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-semibold truncate">{comment.author_name}</span>
          <span className="text-[10px] text-muted-foreground">{date}</span>
        </div>
        <p className="text-xs mt-0.5 leading-relaxed whitespace-pre-wrap break-words">{comment.content}</p>
      </div>
      {isOwnerOrAuthor && (
        <button
          type="button"
          className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive transition-all shrink-0"
          onClick={() => onDelete(comment.id)}
          title="Supprimer"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ── Comments section ─────────────────────────────────────────────────────────
function CommentsSection({ token, shot, accessToken, user, commentsEnabled }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  const loadComments = useCallback(async () => {
    if (!commentsEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const all = await getSharedComments(token, accessToken);
      setComments((all || []).filter((c) => c.shot_id === shot?.id));
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token, accessToken, shot?.id, commentsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadComments(); }, [loadComments]);

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const c = await addSharedComment(token, shot.id, text, accessToken);
      setComments((prev) => [...prev, c]);
      setDraft('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la publication');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteSharedComment(token, commentId, accessToken);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  if (!commentsEnabled) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <MessageSquare className="w-3.5 h-3.5" />
        Commentaires {comments.length > 0 && `(${comments.length})`}
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground/70 py-2">Aucun commentaire pour cette frame.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              onDelete={handleDelete}
              isOwnerOrAuthor={user && (c.author_id === user.user_id)}
            />
          ))}
        </div>
      )}

      {/* Add comment */}
      {user ? (
        <div className="space-y-2">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(); }}
            placeholder="Ajouter un commentaire sur cette frame…"
            maxLength={2000}
            className="w-full min-h-[72px] text-xs rounded-lg border border-input bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary resize-y placeholder:text-muted-foreground/50"
            disabled={submitting}
          />
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-muted-foreground">{draft.length}/2000</span>
            <Button size="sm" className="h-7 text-xs gap-1.5" onClick={handleSubmit} disabled={submitting || !draft.trim()}>
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Publier
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/30 border border-border/50">
          <LogIn className="w-4 h-4 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground">
            <a href="/" className="underline hover:text-foreground transition-colors">Connectez-vous</a> pour laisser un commentaire.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Password gate ────────────────────────────────────────────────────────────
function PasswordGate({ token, onSuccess }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      const data = await verifySharePassword(token, password);
      sessionStorage.setItem(SESSION_KEY(token), data.access_token);
      onSuccess(data);
    } catch (err) {
      setError(err?.response?.status === 401 ? 'Mot de passe incorrect.' : 'Erreur, réessayez.');
    } finally { setLoading(false); }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="w-full max-w-sm mx-4 space-y-6">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Accès protégé</h2>
          <p className="text-sm text-muted-foreground">Ce projet est protégé par un mot de passe.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe…"
            className="w-full h-10 rounded-lg border border-input bg-card px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
            disabled={loading}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <Button type="submit" className="w-full gap-2" disabled={loading || !password}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Accéder
          </Button>
        </form>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function SharedView() {
  const { token } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [commentsEnabled, setCommentsEnabled] = useState(false);
  const [project, setProject] = useState(null);
  const [allowPdf, setAllowPdf] = useState(false);
  const [allowZip, setAllowZip] = useState(false);
  const [allowThumbnails, setAllowThumbnails] = useState(false);
  const [allowEdit, setAllowEdit] = useState(false);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [selectedShot, setSelectedShot] = useState(null);
  const [accessToken, setAccessToken] = useState(
    () => sessionStorage.getItem(SESSION_KEY(token)) || null
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSharedProject(); }, [token, accessToken]);

  const loadSharedProject = async () => {
    setLoading(true);
    try {
      const data = await getSharedProject(token, accessToken);
      if (data.password_required) {
        setPasswordRequired(true);
        setCommentsEnabled(data.comments_enabled ?? false);
        setLoading(false);
        return;
      }
      applyData(data);
    } catch (err) {
      setError("Ce lien de partage n'existe pas ou a expiré.");
    } finally {
      setLoading(false);
    }
  };

  const applyData = (data) => {
    setProject(data.project);
    setAllowPdf(data.allow_pdf_export);
    setAllowZip(data.allow_zip_export);
    setAllowThumbnails(data.allow_thumbnails_export ?? true);
    setAllowEdit(data.allow_edit_export ?? false);
    setAllowDuplicate(data.allow_project_duplicate ?? false);
    setCommentsEnabled(data.comments_enabled ?? false);
    setPasswordRequired(false);
    if (data.project?.shots?.length > 0) setSelectedShot(data.project.shots[0]);
  };

  const handlePasswordSuccess = (data) => {
    setAccessToken(data.access_token);
    applyData(data);
  };

  const handleExport = (format) => {
    let url;
    if (format === 'thumbnails') url = getSharedDownloadZIPThumbnailsUrl(token);
    else if (format === 'zip') url = getSharedDownloadZIPUrl(token);
    else if (format === 'edit') url = getSharedDownloadEditUrl(token);
    else url = getSharedDownloadPDFUrl(token);
    const link = document.createElement('a');
    link.href = url;
    const ext = format === 'pdf' ? 'pdf' : 'zip';
    link.download = `${project.title}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Téléchargement ${({ pdf: 'PDF', zip: 'ZIP Mobile', thumbnails: 'ZIP Vignettes', edit: 'Export Logiciel' })[format] ?? format} lancé !`);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-border mx-auto flex items-center justify-center">
            <Eye className="w-6 h-6 opacity-30" />
          </div>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (passwordRequired) {
    return <PasswordGate token={token} onSuccess={handlePasswordSuccess} />;
  }

  const shotIndex = project.shots?.findIndex((s) => s.id === selectedShot?.id) ?? -1;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="h-14 border-b border-border px-5 flex items-center justify-between bg-card shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-bold text-sm">K</span>
          </div>
          <span className="text-sm text-muted-foreground">Vue partagée</span>
          <span className="text-sm font-semibold">{project.title}</span>
          {commentsEnabled && (
            <span className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground border border-border rounded-full px-2 py-0.5">
              <MessageSquare className="w-3 h-3" /> Commentaires activés
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {allowDuplicate && (
            user ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5"
                disabled={duplicating}
                onClick={async () => {
                  setDuplicating(true);
                  const tid = toast.loading('Duplication en cours…');
                  try {
                    const copy = await duplicateProject(project.id);
                    toast.dismiss(tid);
                    toast.success(`Projet « ${copy.title} » dupliqué dans votre espace`);
                  } catch (err) {
                    toast.dismiss(tid);
                    toast.error(err?.response?.data?.detail || 'Échec de la duplication');
                  } finally { setDuplicating(false); }
                }}
              >
                {duplicating ? <Loader2 className="w-3 h-3 animate-spin" /> : <CopyPlus className="w-3 h-3" />}
                Dupliquer
              </Button>
            ) : (
              <span className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground border border-border rounded-full px-2 py-0.5">
                <CopyPlus className="w-3 h-3" /> Connectez-vous pour dupliquer
              </span>
            )
          )}
          {(allowPdf || allowZip || allowThumbnails || allowEdit) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8 text-xs gap-1.5">
                  <Download className="w-3 h-3" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Format d'export</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {allowPdf && (
                  <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer">
                    <FileText className="w-4 h-4 mr-2" />
                    <div><div className="font-medium">PDF A4</div><div className="text-xs text-muted-foreground">6 vignettes par page</div></div>
                  </DropdownMenuItem>
                )}
                {allowZip && (
                  <DropdownMenuItem onClick={() => handleExport('zip')} className="cursor-pointer">
                    <Smartphone className="w-4 h-4 mr-2" />
                    <div><div className="font-medium">ZIP Mobile</div><div className="text-xs text-muted-foreground">Images 9:16 pour téléphone</div></div>
                  </DropdownMenuItem>
                )}
                {allowThumbnails && (
                  <DropdownMenuItem onClick={() => handleExport('thumbnails')} className="cursor-pointer">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    <div><div className="font-medium">ZIP Vignettes</div><div className="text-xs text-muted-foreground">Uniquement les images brutes</div></div>
                  </DropdownMenuItem>
                )}
                {allowEdit && (
                  <DropdownMenuItem onClick={() => handleExport('edit')} className="cursor-pointer">
                    <Clapperboard className="w-4 h-4 mr-2" />
                    <div><div className="font-medium">Export Logiciel</div><div className="text-xs text-muted-foreground">FCPXML + médias en ZIP</div></div>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <ThemeToggle />
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Shots Grid */}
        <div className="flex-1 overflow-auto p-6">
          <div className="mb-4 text-sm text-muted-foreground">
            {project.shots?.length || 0} plans •{' '}
            {formatDuration(project.shots?.reduce((a, s) => a + (s.duration || 0), 0) || 0)}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {project.shots?.map((shot, index) => (
              <div
                key={shot.id}
                className={`bg-card border rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-md ${selectedShot?.id === shot.id ? 'ring-2 ring-primary' : 'border-border'}`}
                onClick={() => setSelectedShot(shot)}
              >
                <div className="aspect-video bg-secondary/30 relative">
                  {shot.frame_path ? (
                    <img src={API_URL + shot.frame_path} alt={`Plan ${index + 1}`} className={`w-full h-full ${project.format === '9:16' ? 'object-contain' : 'object-cover'}`} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-6 h-6 text-muted-foreground/20" />
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">#{index + 1}</span>
                    <span className="text-muted-foreground">{formatDuration(shot.duration)}</span>
                  </div>
                  {shot.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{shot.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Inspector */}
        {selectedShot && (
          <div className="w-80 border-l border-border bg-card overflow-y-auto p-4 space-y-4">
            <div className="text-sm font-semibold">
              Plan #{shotIndex + 1}
            </div>

            {selectedShot.frame_path && (
              <div className="aspect-video bg-secondary/30 rounded-lg overflow-hidden">
                <img src={API_URL + selectedShot.frame_path} alt="Aperçu du plan" className={`w-full h-full ${project.format === '9:16' ? 'object-contain' : 'object-cover'}`} />
              </div>
            )}

            {/* Metadata */}
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-1">Durée</div>
                  <div className="bg-secondary/30 rounded-md px-2 py-1.5">{formatDuration(selectedShot.duration)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-1">Type</div>
                  <div className="bg-secondary/30 rounded-md px-2 py-1.5">{selectedShot.shot_type || '-'}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-1">Caméra</div>
                  <div className="bg-secondary/30 rounded-md px-2 py-1.5">{selectedShot.camera_movement || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-1">Focale</div>
                  <div className="bg-secondary/30 rounded-md px-2 py-1.5">{selectedShot.focal_length || '-'}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase mb-1">Éclairage</div>
                <div className="bg-secondary/30 rounded-md px-2 py-1.5">{selectedShot.lighting || '-'}</div>
              </div>
              {selectedShot.description && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-1">Description</div>
                  <div className="bg-secondary/30 rounded-md px-2 py-1.5 whitespace-pre-wrap">{selectedShot.description}</div>
                </div>
              )}
              {selectedShot.dialogue && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-1">Dialogue</div>
                  <div className="bg-secondary/30 rounded-md px-2 py-1.5 whitespace-pre-wrap italic">"{selectedShot.dialogue}"</div>
                </div>
              )}
              {selectedShot.notes && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase mb-1">Notes</div>
                  <div className="bg-secondary/30 rounded-md px-2 py-1.5 whitespace-pre-wrap">{selectedShot.notes}</div>
                </div>
              )}
            </div>

            {/* Comments */}
            <div className="border-t border-border/50 pt-4">
              <CommentsSection
                token={token}
                shot={selectedShot}
                accessToken={accessToken}
                user={user}
                commentsEnabled={commentsEnabled}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
